import * as Sentry from '@sentry/react';

/**
 * Browser-side GlitchTip (Sentry-protocol) error tracking for the dashboard.
 *
 * The API counterpart lives in `apps/api/src/instrument.ts` (@sentry/bun); a
 * browser bundle cannot use that SDK, so this is the browser twin of the same
 * config — error-only, no tracing, no replay — not a second tracking system.
 *
 * The DSN comes from `VITE_GLITCHTIP_DSN` at build time. Complete no-op when it
 * is unset: `Sentry.init` is never called, so nothing is instrumented and no
 * network request is ever made.
 */

let initialized = false;

/** Keys/params whose values are stripped before an event leaves the browser. */
const SENSITIVE_KEY_PATTERN =
  /(cookie|authorization|auth|token|secret|password|api[-_]?key|apikey|session|credential|signature|bearer|body|payload|header|captured)/i;

const REDACTED = '[redacted]';

/**
 * Strip the query string from a URL. Dashboard URLs are mostly opaque ids, but
 * the API client puts the static API key in a header and auth flows put tokens
 * in the query string — neither belongs in a report.
 */
export function sanitizeUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    // Base only matters for relative URLs; falls back so this stays testable
    // outside a browser.
    const base = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const parsed = new URL(url, base);
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return url.split('?')[0];
  }
}

function redact(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 3) return '[truncated]';
  if (typeof value === 'string') return value.length > 1000 ? `${value.slice(0, 1000)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => redact(v, depth + 1));
  if (value instanceof Error) return { name: value.name, message: value.message };
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY_PATTERN.test(k) ? REDACTED : redact(v, depth + 1);
    }
    return out;
  }
  return `[${typeof value}]`;
}

/**
 * Exported for tests: the `beforeSend` body. Untyped on purpose — the event
 * shape varies by SDK version and this must keep working on fields it does not
 * know about.
 */
export function scrubEvent(event: Record<string, any>): Record<string, any> {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.url) event.request.url = sanitizeUrl(event.request.url);
  }
  if (event.extra) event.extra = redact(event.extra) as Record<string, unknown>;
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((crumb: Record<string, any>) => {
      if (!crumb?.data) return crumb;
      const data = { ...crumb.data };
      if (typeof data.url === 'string') data.url = sanitizeUrl(data.url);
      return { ...crumb, data: redact(data) as Record<string, unknown> };
    });
  }
  return event;
}

export function initGlitchtip(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const dsn = import.meta.env.VITE_GLITCHTIP_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    // Send envelopes to our own origin instead of straight to GlitchTip. Content
    // blockers match on the `sentry_key` query parameter the SDK appends to the
    // direct ingest URL, so direct sends fail for any user running one — a
    // first-party GlitchTip domain does NOT help. The API relays it upstream;
    // see apps/api/src/lib/glitchtip-tunnel.ts.
    tunnel: '/api/t',
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GLITCHTIP_RELEASE as string | undefined,
    // Error-only, matching the API: no tracing/APM, no replay.
    // Default integrations stay on deliberately — `globalHandlers` is what turns
    // an uncaught error or unhandled rejection into a reported event.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    maxBreadcrumbs: 30,
    beforeSend: (event) => scrubEvent(event as Record<string, any>) as typeof event,
  });

  Sentry.setTag('service', 'databro-web');
  initialized = true;
  console.log('✅ GlitchTip browser error tracking initialized');
}

export function isGlitchtipEnabled(): boolean {
  return initialized;
}

/**
 * Attach the signed-in identity to every subsequent event, so a report reads
 * "who hit this" instead of "some browser". Passing null clears it (sign-out).
 * Email is an internal-team identifier here; no other profile data is sent.
 */
export function setGlitchtipUser(user: { id?: string; email?: string } | null): void {
  if (!initialized) return;
  Sentry.setUser(user ? { id: user.id, username: user.email } : null);
}

/** Where a client error was intercepted. Becomes the `error_source` tag. */
export type ClientErrorSource =
  | 'react-error-boundary'
  | 'react-query'
  | 'react-mutation'
  | 'websocket'
  | 'manual';

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(`Non-Error thrown: ${JSON.stringify(value)}`);
  } catch {
    return new Error('Non-Error thrown: <unserializable>');
  }
}

/**
 * The single funnel for reporting a caught client-side error.
 *
 * Errors thrown during React rendering never reach `window.onerror` — a boundary
 * swallows them — so the SDK's global handlers do NOT see them. Anything caught
 * by a boundary (or by TanStack Query) must be handed here explicitly, otherwise
 * the user sees a fallback screen and GlitchTip sees nothing.
 *
 * Always logs to the console too, so the error stays visible when no DSN is
 * configured (local dev, or a misconfigured deploy).
 */
export function reportClientError(
  error: unknown,
  context: { source: ClientErrorSource } & Record<string, unknown>
): void {
  const err = toError(error);
  const { source, ...rest } = context;

  console.error(`[client-error:${source}]`, err, rest);

  if (!initialized) return;

  Sentry.captureException(err, {
    tags: { error_source: source },
    extra: {
      ...rest,
      page_path: window.location.pathname,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
  });
}
